/** @fileoverview Displays an app card with its editor, status, and actions. */

import { useEffect, useState } from 'react';
import { Copy, Ellipsis, ExternalLink, FileText, Trash2 } from 'lucide-react';
import { getHostedAppUrl } from '../../appMode/hostConfig';
import { describePastTime } from '../../lib/relativeTime';
import type { ConnectedSystem } from '../../services/systemsApi';
import type { FabrikAppSummary } from '../../types/apps';
import type { User } from '../../types/identity';
import { initials } from '../AccountBadge';
import ConnectedSystemDetails from '../ConnectedSystemDetails';
import AppBrandMark from '../AppRenderer/AppBrandMark';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

/** Reads initials from a display name, which is a full name or, without one, an email. */
const nameInitials = (name: string) => {
  const nameParts = name.trim().split(/\s+/);
  return name.includes('@')
    ? initials({ email: name })
    : initials({
        first_name: nameParts[0],
        last_name: nameParts.length > 1 ? nameParts.at(-1) : null,
      });
};

const emailAddress = (value?: string | null) =>
  value && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim()) ? value.trim().toLowerCase() : '';

function useGravatar(email: string) {
  const [url, setUrl] = useState('');

  useEffect(() => {
    let cancelled = false;
    setUrl('');
    if (!email || !globalThis.crypto?.subtle) return () => undefined;

    void crypto.subtle
      .digest('SHA-256', new TextEncoder().encode(email))
      .then((digest) => {
        const hash = [...new Uint8Array(digest)]
          .map((byte) => byte.toString(16).padStart(2, '0'))
          .join('');
        if (!cancelled) setUrl(`https://gravatar.com/avatar/${hash}?d=404&s=64`);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [email]);

  return url;
}

type Props = {
  app: FabrikAppSummary;
  tenantName: string;
  systems: ConnectedSystem[];
  user?: User;
  duplicatingAppId: string | null;
  onDuplicate: () => void;
  onDelete: () => void;
  onOpen: () => void;
};

export default function AppCard({
  app,
  tenantName,
  systems,
  user,
  duplicatingAppId,
  onDuplicate,
  onDelete,
  onOpen,
}: Props) {
  const {
    id,
    name,
    slug,
    description,
    production_app_version_id: productionVersionId,
    publishedVersion,
    hasUnpublishedChanges,
    updated_at: updatedAt,
    updated_by: updatedBy,
    updatedByName,
    updatedByProfilePicture,
    systemIds = [],
    theme,
    appearance,
  } = app;
  const lastEdit = describePastTime(updatedAt);
  const isLive = Boolean(publishedVersion ?? productionVersionId);
  const hasPendingChanges = isLive && hasUnpublishedChanges === true;
  const liveUrl = isLive ? getHostedAppUrl({ slug, preview: false }) : '';
  const editorEmail = emailAddress(updatedBy) || emailAddress(updatedByName);
  const currentUserName = [user?.first_name, user?.last_name].filter(Boolean).join(' ');
  const editorIsViewer = Boolean(
    user &&
      ((editorEmail && editorEmail === user.email?.trim().toLowerCase()) ||
        (updatedByName &&
          currentUserName &&
          updatedByName.trim().toLowerCase() === currentUserName.toLowerCase()))
  );
  const editorImage = updatedByProfilePicture || (editorIsViewer ? user?.picture : undefined);
  const gravatarUrl = useGravatar(editorImage ? '' : editorEmail);
  const editorName = updatedByName || (editorIsViewer ? currentUserName : '') || editorEmail;
  const appLogo = theme?.logo;

  const hasAppLogo =
    appLogo?.type === 'typography' ||
    appLogo?.type === 'icon' ||
    (appLogo?.type === 'custom' && appearance?.logo?.type === 'image');

  return (
    <article className="relative flex min-h-44 flex-col rounded-xl border border-app-border bg-app-surface-card px-4 pt-3.5 pb-2.5 shadow-sm transition-shadow hover:shadow-md">
      <div className="flex items-start gap-3">
        {hasAppLogo ? (
          <AppBrandMark
            tenantName={tenantName}
            appearance={appearance ?? undefined}
            logo={appLogo}
            logoColor={appLogo.color || theme?.primaryColor}
            className="grid size-9 shrink-0 place-items-center rounded-lg bg-app-brand-subtle text-app-branch"
          />
        ) : (
          <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-app-brand-subtle text-app-branch">
            <FileText className="h-5 w-5" aria-hidden="true" />
          </span>
        )}
        <h2 className="min-w-0 flex-1 pt-1 text-sm font-semibold leading-5 text-app-text">
          <button
            type="button"
            className="text-left after:absolute after:inset-0 after:rounded-xl focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-app-brand-strong"
            onClick={onOpen}
          >
            {name}
          </button>
        </h2>
        {isLive ? (
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-app-brand-subtle px-2 py-1 text-xs font-semibold text-app-brand-strong">
            <span className="size-1.5 rounded-full bg-app-brand-strong" aria-hidden="true" />
            Published
          </span>
        ) : (
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-app-surface-muted-strong px-2 py-1 text-xs font-medium text-app-text-muted">
            <span className="size-1.5 rounded-full bg-app-text-subtle" aria-hidden="true" />
            Draft
          </span>
        )}
      </div>
      <p className="mt-2 line-clamp-2 min-h-9 text-xs leading-4.5 text-app-text-muted">
        {description || 'No description yet.'}
      </p>
      <ConnectedSystemDetails
        systems={systems}
        systemIds={systemIds}
        typeOnly
        className="mt-2 mb-2"
      />
      {hasPendingChanges && <p className="mt-1 text-xs text-app-text-muted">Unpublished changes</p>}
      <div className="mt-auto flex min-w-0 items-center gap-2 border-t border-app-border pt-2.5">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {editorName && (
            <Avatar size="sm" aria-hidden="true">
              {editorImage || gravatarUrl ? (
                <AvatarImage src={editorImage || gravatarUrl} alt="" />
              ) : null}
              <AvatarFallback className="bg-app-avatar text-xs font-semibold text-app-action-foreground">
                {nameInitials(editorName)}
              </AvatarFallback>
            </Avatar>
          )}
          <div className="min-w-0 text-xs leading-4">
            {editorName && <p className="truncate font-medium text-app-text">{editorName}</p>}
            {lastEdit.label && (
              <p className="truncate text-app-text-muted">
                {editorName ? 'Edited ' : 'Last edited '}
                <time dateTime={updatedAt} title={lastEdit.fullDate}>
                  {lastEdit.label}
                </time>
              </p>
            )}
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="relative z-10 h-8 shrink-0 gap-2 border-app-border-strong px-2.5 text-xs text-app-text"
          onClick={() =>
            liveUrl ? window.open(liveUrl, '_blank', 'noopener,noreferrer') : onOpen()
          }
        >
          {liveUrl ? 'Open app' : 'Continue editing'}
          {liveUrl ? (
            <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
          ) : (
            <span aria-hidden="true">→</span>
          )}
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger
            type="button"
            aria-label={`Actions for ${name}`}
            className="relative z-10 grid size-8 shrink-0 place-items-center rounded-md text-app-text-muted hover:bg-app-surface-muted-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-app-brand-strong"
          >
            <Ellipsis className="h-5 w-5" aria-hidden="true" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem disabled={Boolean(duplicatingAppId)} onClick={onDuplicate}>
              <Copy aria-hidden="true" />
              {duplicatingAppId === id ? 'Duplicating…' : 'Duplicate'}
            </DropdownMenuItem>
            <DropdownMenuItem variant="destructive" onClick={onDelete}>
              <Trash2 aria-hidden="true" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </article>
  );
}
